import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wallet, ArrowDownRight, ArrowUpRight, Plus } from "lucide-react";
import BackButton from "@/components/BackButton";

export default function WalletPage() {
  const { user, refresh } = useAuth();
  const [state, setState] = useState({ balance: 0, transactions: [] });
  const [amount, setAmount] = useState("50");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/wallet/me");
    setState(data);
  };

  useEffect(() => {
    load();
  }, []);

  const topup = async () => {
    const n = parseFloat(amount);
    if (!n || n <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    try {
      await api.post("/wallet/topup", { amount: n });
      toast.success(`Topped up $${n.toFixed(2)}`);
      load();
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Top-up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          /wallet
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter">
          Student Wallet
        </h1>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Wallet card */}
        <div className="relative col-span-1 lg:col-span-2" data-testid="wallet-card">
          <div className="border-2 border-foreground bg-foreground p-7 text-background hard-shadow">
            <div className="flex items-start justify-between">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-background/60">
                  Balance · USD
                </div>
                <div className="font-display mt-2 text-5xl font-extrabold tracking-tighter">
                  ${state.balance.toFixed(2)}
                </div>
              </div>
              <div className="flex h-12 w-12 items-center justify-center bg-background text-foreground">
                <Wallet className="h-6 w-6" />
              </div>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4 border-t border-background/20 pt-4">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-background/60">
                  Account
                </div>
                <div className="mt-1 font-mono text-xs">
                  {user?.id?.slice(0, 8)}…{user?.id?.slice(-4)}
                </div>
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-background/60">
                  Network
                </div>
                <div className="mt-1 font-mono text-xs">polygon (sim)</div>
              </div>
            </div>
          </div>
        </div>

        {/* Top up */}
        <div className="border-2 border-foreground bg-background p-6" data-testid="topup-card">
          <div className="font-display text-lg font-extrabold tracking-tight">
            Top up wallet
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Add funds to your simulated wallet.
          </p>
          <div className="mt-4 flex gap-2">
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              className="rounded-none border-foreground"
              data-testid="topup-amount-input"
              placeholder="50"
            />
            <Button
              onClick={topup}
              disabled={busy}
              className="rounded-none bg-foreground text-background hard-shadow-sm"
              data-testid="topup-btn"
            >
              <Plus className="mr-1 h-4 w-4" /> Top up
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {[25, 100, 500, 1000].map((v) => (
              <button
                key={v}
                onClick={() => setAmount(String(v))}
                className="mono border border-foreground px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background"
                data-testid={`preset-${v}`}
              >
                ${v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Transactions */}
      <div className="mt-10">
        <div className="mb-4">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            /history
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">
            Transactions
          </h2>
        </div>
        <div className="border-2 border-foreground bg-background">
          <div className="mono grid grid-cols-12 gap-2 border-b border-border bg-muted px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            <div className="col-span-2">Type</div>
            <div className="col-span-5">Description</div>
            <div className="col-span-3">Timestamp</div>
            <div className="col-span-2 text-right">Amount</div>
          </div>
          {state.transactions.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No transactions yet.
            </div>
          ) : (
            state.transactions.map((t) => {
              const credit = t.amount >= 0;
              return (
                <div
                  key={t.id}
                  className="grid grid-cols-12 items-center gap-2 border-b border-border px-4 py-3 text-sm last:border-b-0"
                >
                  <div className="col-span-2 flex items-center gap-2">
                    {credit ? (
                      <ArrowDownRight className="h-4 w-4 text-accent" />
                    ) : (
                      <ArrowUpRight className="h-4 w-4 text-destructive" />
                    )}
                    <span className="mono text-[10px] uppercase">{t.type}</span>
                  </div>
                  <div className="col-span-5">{t.description}</div>
                  <div className="col-span-3 mono text-xs text-muted-foreground">
                    {new Date(t.created_at).toLocaleString()}
                  </div>
                  <div
                    className={`col-span-2 text-right font-mono font-semibold ${
                      credit ? "text-accent" : "text-destructive"
                    }`}
                  >
                    {credit ? "+" : ""}
                    ${Math.abs(t.amount).toFixed(2)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
